import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { clientsAPI } from '../services/api.js';
import { Calendar, Users, LogOut, X, Plus, Edit, Trash2, Link, Mail, Phone, Building } from 'lucide-react';
import './ClientsPage.css';
import ClientCredentialsModal from '../components/ClientCredentialsModal';

const ClientsPage = () => {
  const { user, token, logout } = useAuth();
  const navigate = useNavigate();
  
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [selectedClient, setSelectedClient] = useState(null);
  const [showCredentialsModal, setShowCredentialsModal] = useState(false);
  
  const [formData, setFormData] = useState({
    client_name: '',
    client_email: '',
    client_phone: '',
    client_company_name: '',
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone, // ✅ ADD
    notes: ''
  });

  useEffect(() => {
    if (user?.user_type !== 'organization') {
      return;
    }
    fetchClients();
  }, [user, token]);

  const fetchClients = async () => {
    try {
      setLoading(true);
      const response = await clientsAPI.getMyClients(token);
      if (response.success) {
        setClients(response.clients);
      }
    } catch (err) {
      console.error('Error fetching clients:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async () => {
    if (!formData.client_name) {
      alert('Client name is required');
      return;
    }

    try {
      if (editingId) {
        const result = await clientsAPI.updateClient(editingId, formData, token);
        if (result.success) {
          alert('✅ Client updated successfully');
          handleCloseModal();
          fetchClients();
        }
      } else {
        const result = await clientsAPI.addClient(formData, token);
        if (result.success) {
          alert('✅ Client added successfully');
          handleCloseModal();
          fetchClients();
        }
      }
    } catch (err) {
      alert('Error: ' + (err.message || 'Something went wrong'));
    }
  };

  const handleEdit = (client) => {
    setEditingId(client.id);
    setFormData({
      client_name: client.client_name,
      client_email: client.client_email || '',
      client_phone: client.client_phone || '',
      client_company_name: client.client_company_name || '',
      timezone: client.timezone || 'UTC', // ✅ ADD
      notes: client.notes || ''
    });
    setShowModal(true);
  };

  const handleDelete = async (clientId) => {
    if (window.confirm('Are you sure you want to delete this client?')) {
      try {
        const result = await clientsAPI.deleteClient(clientId, token);
        if (result.success) {
          alert('✅ Client deleted successfully');
          fetchClients();
        }
      } catch (err) {
        alert('Error: ' + (err.message || 'Failed to delete client'));
      }
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingId(null);
    setFormData({
      client_name: '',
      client_email: '',
      client_phone: '',
      client_company_name: '',
      notes: ''
    });
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  if (user?.user_type !== 'organization') {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <h2>This page is only for organization users</h2>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="calendar-dashboard">
        <div className="sidebar">
          <div className="sidebar-header">
            <h2>
              <Calendar size={24} />
              QuickPost
            </h2>
          </div>
        </div>
        <div className="main-content" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ fontSize: '18px', color: '#605E5C' }}>Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="calendar-dashboard">
      {/* Left Sidebar */}
      <div className="sidebar">
        <div className="sidebar-header">
          <h2>
            <Calendar size={24} />
            QuickPost
          </h2>
        </div>

        <button className="btn-new-post" onClick={() => setShowModal(true)}>
          <Plus size={18} />
          New Client
        </button>

        <nav className="sidebar-nav">
          <div className="nav-item" onClick={() => navigate('/dashboard')}>
            <Calendar size={20} />
            Calendar
          </div>
          
          <div className="nav-item active">
            <Users size={20} />
            Manage Clients
          </div>
        </nav>

        <div className="sidebar-footer">
          <div className="user-profile">
            <div className="user-avatar">
              {user?.name?.split(' ').map(n => n[0]).join('').toUpperCase()}
            </div>
            <div className="user-info">
              <div className="user-name">{user?.name}</div>
              <div className="user-type">🏢 Organization</div>
            </div>
          </div>
          <button className="btn-logout" onClick={handleLogout}>
            <LogOut size={16} />
            Sign out
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="main-content">
        {/* Top Header */}
        <div className="calendar-header">
          <div className="header-controls">
            <h1 className="page-title">Manage Clients</h1>
          </div>
          <div className="post-count">
            {clients.length} {clients.length === 1 ? 'client' : 'clients'}
          </div>
        </div>

        {/* Clients Grid */}
        <div className="clients-content">
          {clients.length === 0 ? (
            <div className="no-clients">
              <Users size={64} color="#C8C6C4" />
              <h3>No clients yet</h3>
              <p>Add your first client to get started!</p>
              <button className="btn-add-first" onClick={() => setShowModal(true)}>
                <Plus size={18} />
                Add First Client
              </button>
            </div>
          ) : (
            <div className="clients-grid">
              {clients.map(client => (
                <div key={client.id} className="client-card-modern">
                  <div className="client-card-header">
                    <div className="client-avatar">
                      {client.client_name.substring(0, 2).toUpperCase()}
                    </div>
                    <div className="client-title">
                      <h3>{client.client_name}</h3>
                      <span className={`status-badge status-${client.status}`}>
                        {client.status}
                      </span>
                    </div>
                  </div>

                  <div className="client-details">
                    {client.client_company_name && (
                      <div className="detail-row">
                        <Building size={16} />
                        <span>{client.client_company_name}</span>
                      </div>
                    )}
                    {client.client_email && (
                      <div className="detail-row">
                        <Mail size={16} />
                        <span>{client.client_email}</span>
                      </div>
                    )}
                    {client.client_phone && (
                      <div className="detail-row">
                        <Phone size={16} />
                        <span>{client.client_phone}</span>
                      </div>
                    )}
                  </div>

                  {client.notes && (
                    <div className="client-notes">
                      <p>{client.notes}</p>
                    </div>
                  )}

                  <div className="client-actions-modern">
                    <button 
                      className="btn-action btn-connect"
                      onClick={() => {
                        setSelectedClient(client);
                        setShowCredentialsModal(true);
                      }}
                      title="Connect Social Accounts"
                    >
                      <Link size={16} />
                      Connect
                    </button>
                    <button 
                      className="btn-action btn-edit"
                      onClick={() => handleEdit(client)}
                      title="Edit Client"
                    >
                      <Edit size={16} />
                    </button>
                    <button 
                      className="btn-action btn-delete"
                      onClick={() => handleDelete(client.id)}
                      title="Delete Client"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Add/Edit Client Modal */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2>{editingId ? 'Edit Client' : 'Add New Client'}</h2>
              <button className="btn-close" onClick={handleCloseModal}>
                <X size={24} />
              </button>
            </div>

            <div className="modal-body">
              <div className="form-group">
                <label>Client Name *</label>
                <input
                  type="text"
                  name="client_name"
                  value={formData.client_name}
                  onChange={handleInputChange}
                  placeholder="Enter client name"
                />
              </div>

              <div className="form-group">
                <label>Company Name</label>
                <input
                  type="text"
                  name="client_company_name"
                  value={formData.client_company_name}
                  onChange={handleInputChange}
                  placeholder="Enter company name"
                />
              </div>

              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  name="client_email"
                  value={formData.client_email}
                  onChange={handleInputChange}
                  placeholder="client@example.com"
                />
              </div>

              <div className="form-group">
                <label>Phone</label>
                <input
                  type="tel"
                  name="client_phone"
                  value={formData.client_phone}
                  onChange={handleInputChange}
                  placeholder="+1 234 567 8900"
                />
              </div>

              <div className="form-group">
                <label>Client Timezone *</label>
                <select
                  name="timezone"
                  value={formData.timezone}
                  onChange={handleInputChange}
                  required
                >
                  <option value="Asia/Kolkata">Asia/Kolkata</option>
                  <option value="Europe/London">Europe/London</option>
                  <option value="America/New_York">America/New_York</option>
                  <option value="America/Los_Angeles">America/Los_Angeles</option>
                  <option value="Australia/Sydney">Australia/Sydney</option>
                </select>
              </div>


              <div className="form-group">
                <label>Notes</label>
                <textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleInputChange}
                  rows="3"
                  placeholder="Additional notes about the client..."
                />
              </div>

              <div className="modal-actions">
                <button className="btn-cancel" onClick={handleCloseModal}>
                  Cancel
                </button>
                <button className="btn-submit" onClick={handleSubmit}>
                  {editingId ? 'Update Client' : 'Add Client'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Credentials Modal */}
      {showCredentialsModal && selectedClient && (
        <ClientCredentialsModal
          client={selectedClient}
          onClose={() => {
            setShowCredentialsModal(false);
            setSelectedClient(null);
          }}
        />
      )}
    </div>
  );
};

export default ClientsPage;